import React from 'react'
import cx from 'classnames'
import { useBetTokenBalance, useDetailedBetslip } from '@azuro-org/sdk'
import { Message } from '@locmod/intl'
import { formatToFixed } from 'helpers/formatters'

import { getQuickBetStorageValue } from '../QuickBet/QuickBet'

import messages from './messages'
import { useWallet } from 'wallet'
import { useDetailedOvertimeBetslip } from '@/contexts/DetailedOvertimeBetslipContext'
import { useBalance, useChainId } from 'wagmi'
import { useOvertimeBet } from '@/hooks/useOvertimeBet'
import { useTicket } from '@/contexts/TicketContext'
import { type SupportedNetwork } from '@/Overtime/types/network'
import multipleCollateralContract from '@/Overtime/utils/contracts/multipleCollateralContract'

type OvertimeChipProps = {
  title: string | Intl.Message
  isActive: boolean
  isDisabled?: boolean
  onClick: () => void
}

const OvertimeChip: React.FC<OvertimeChipProps> = ({
  title,
  isActive,
  isDisabled,
  onClick,
}) => {
  const className = cx(
    'px-3 py-2 text-center w-full rounded-t-xs rounded-b-min',
    {
      'bg-brand-light-500 text-black': isActive,
      'text-accent-grey-100 bg-accent-grey-800 hover:text-brand-light-500':
        !isActive,
      'cursor-now-allowed text-white': isDisabled,
    }
  )

  return (
    <button className={className} onClick={onClick}>
      <Message
        className="text-caption-12 font-medium uppercase"
        value={title}
      />
    </button>
  )
}

const OvertimeChips: React.FC = () => {
  const { account: address } = useWallet()
  const { betAmount, changeBetAmount } = useDetailedOvertimeBetslip()
  const { ticket, state } = useTicket()
  const chainId = useChainId()

  // Always call useOvertimeBet - the hook handles chain support internally
  const { maxBet } = useOvertimeBet({
    ticket,
    betAmount,
    selectedCollateralIndex: state.payment.selectedCollateralIndex,
  })

  const { data, isFetching: isBalanceLoading } = useBalance({
    address,
    token: multipleCollateralContract.USDC.addresses[
      chainId as SupportedNetwork
    ] as `0x${string}`,
  })

  const { formatted: balance } = data || {}
  if (!address) {
    return null
  }

  const chips = getQuickBetStorageValue()

  const maxBetAmount = Math.min(maxBet || Infinity, Number(balance) || 0)

  return (
    <div className="flex items-center space-x-[2px] mt-1">
      {chips.map(chipValue => {
        const isActive = +betAmount === +chipValue

        return (
          <OvertimeChip
            key={chipValue}
            title={String(chipValue)}
            isActive={isActive}
            onClick={() => changeBetAmount(String(chipValue))}
          />
        )
      })}
      <OvertimeChip
        title={messages.max}
        isActive={
          Boolean(+betAmount) && +formatToFixed(balance || 0, 2) === +betAmount
        }
        isDisabled={isBalanceLoading}
        onClick={() => changeBetAmount(maxBetAmount.toString())}
      />
    </div>
  )
}

export default OvertimeChips
